import { FileModel } from "../hickory-api";
import { fixDate } from "../shared/pipes/fix-date/fix-date.pipe";


export interface FileModelView extends FileModel {
  isNew?: boolean;
  label?: string;
  hint?: string;
  instructions?: string;
  isImageFile?: boolean;
  alt?: string;
}

export class FileModelHelper {
  public static blobToFileModel(blob: Blob, data: string, fileName?: string, isManagementView: boolean = false): FileModelView {
    if (!fileName) {
      fileName = blob instanceof File ? blob.name : `uploaded-file-${crypto.randomUUID()}.${blob.type.split('/')[1]}`;
    }
    const fileModel: FileModel = {
      FileName: fileName,
      PublicUrl: data,
      ContentType: blob.type,
      Extension: fileName.split('.').pop(),
      Length: blob.size,
      FileSize: this.formatFileSize(blob.size),
      Folder: null,
      Description: null
    };
    return this.applyFileViewOverrides(fileModel, true, isManagementView);
  }

  public static async fileToFileModel(file: File, isManagementView: boolean): Promise<FileModelView> {
    // Read file as Data URL, set PublicUrl, then return the model
    const getUrl = new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        const url = e.target.result as string;
        resolve(url);
      };
      reader.onerror = (err) => {
        reject(err);
      };
      reader.readAsDataURL(file);
    });
    const publicUrl = await getUrl;
    return this.blobToFileModel(file, publicUrl, file.name, isManagementView);
  }

  public static isImageFile(fileModel: FileModel): boolean {
    if (!fileModel?.ContentType) return false;
    return this.getContentType(fileModel) === 'image';
  }

  public static fileHasExtension(input: FileModel | File): boolean {
    const fileName = input instanceof File ? input.name : input.FileName;
    return this.fileNameHasExtension(fileName);
  }

  public static fileNameHasExtension(fileName: string | null): boolean {
    return !!this.fileNameExtension(fileName);
  }

  public static fileExtension(input: FileModel | File): string | null {
    const fileName = input instanceof File ? input.name : input.FileName;
    return this.fileNameExtension(fileName);
  }

  public static fileNameExtension(fileName: string | null): string | null {
    if (!fileName) return null;
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex <= 0 || lastDotIndex === fileName.length - 1) return null;
    return fileName.substring(lastDotIndex + 1);
  }

  public static getContentType(fileModel: FileModel) {
    if (fileModel.ContentType) {
      if (fileModel.ContentType.toLowerCase().startsWith('image')) {
        return 'image';
      }
      if (fileModel.ContentType.toLowerCase().startsWith('audio')) {
        return 'audio';
      }
      if (fileModel.ContentType.toLowerCase().startsWith('video')) {
        return 'video';
      }
    }
    return 'file';
  }

  public static applyFileViewOverrides(fileModel: FileModelView, isNew = false, isManagementView = false): FileModelView {
    if(!fileModel) return null;
    const isImageFile = this.isImageFile(fileModel);
    return {
      ...fileModel,
      hint: this.getDescriptionHint(fileModel),
      label: this.getDescriptionLabel(fileModel),
      instructions: this.getDescriptionInstructions(isImageFile, isNew, isManagementView),
      isImageFile: isImageFile,
      alt: this.getAltText(fileModel),
      isNew
    };
  }

  public static getAltText(fileModel: FileModel, defaultAltText = ''): string {
    return fileModel.Description || fileModel.FileName || defaultAltText;
  }

  public static getIcon(fileModel: FileModel): string {
    if (!fileModel) return '';
    const contentType = this.getContentType(fileModel);
    switch (contentType) {
      case 'image': return 'image';
      case 'audio': return 'audio_file';
      case 'video': return 'video_file';
      default: return 'attachment';
    }
  }

  public static getDate(fileModel: FileModel): Date | null {
    return fixDate(fileModel.DateCreated);
  }

  public static formatFileSize(size: number): string {
    if (typeof size !== 'number' || isNaN(size)) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let unitIndex = 0;
    let fileSize = size;
    while (fileSize >= 1024 && unitIndex < units.length - 1) {
      fileSize /= 1024;
      unitIndex++;
    }
    return `${fileSize.toFixed(1)} ${units[unitIndex]}`;
  }

  public static getDescriptionHint(fileModel: FileModelView): string {
    return this.isImageFile(fileModel) ? 'Alert.Attachments.AltTextHint' : 'Alert.Attachments.DescriptionHint';
  }

  public static getDescriptionLabel(fileModel: FileModelView): string {
    return this.isImageFile(fileModel) ? 'Alert.Attachments.AltTextLabel' : 'Alert.Attachments.DescriptionLabel';
  }

  public static getDescriptionInstructions(isImageFile: boolean, isNew: boolean, isManagementView: boolean): string {
    if(isImageFile && !isManagementView) {
      return isNew ? 'Alert.Attachments.UploadAddAltTextInstructions' : 'Alert.Attachments.ExistingAddAltTextInstructions';
    }
    return '';
  }
}
